/**
 * CurrentArchitectureView - Read-only view of architecture configuration
 * 
 * Features:
 * - Collapsible sections with constraint text lists
 * - Preview JSON button for MCP context output
 * - Shows active templates based on DNA config
 * - Styled constraints with left border
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { 
  Loader2, CheckCircle, AlertTriangle, ChevronDown, ChevronRight,
  CheckCircle2, Eye, Copy, Check, Code, History, Info
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { deliveryConfigApi } from '../../services/api';
import { toast } from 'sonner';

// System default templates - matches backend SYSTEM_DEFAULT_TEMPLATES
const SYSTEM_DEFAULT_TEMPLATES = {
  // TENANCY MODE
  b2c: [
    "INFO: Single-tenant consumer application. Standard access control applies."
  ],
  b2b_multitenant: [
    "CRITICAL: MULTITENANT system. Every database query MUST filter by 'org_id' or 'tenant_id'.",
    "CRITICAL: Implement middleware to validate tenant access before any data operation.",
    "TEST: Generate cross-tenant access tests that MUST fail."
  ],
  
  // COMPLIANCE
  hipaa: [
    "CRITICAL: HIPAA compliance active. Encrypt all PII/PHI fields at rest (SSN, DOB, diagnosis, etc.).",
    "CRITICAL: Do NOT log request/response payloads - they may contain PHI.",
    "CRITICAL: All WRITE/DELETE operations MUST create immutable audit entry.",
    "CONSTRAINT: Session timeout must be 15 minutes or less."
  ],
  pci: [
    "CRITICAL: PCI-DSS active. Never store raw credit card numbers - use tokenization.",
    "CRITICAL: All payment operations MUST log to immutable audit trail.",
    "CONSTRAINT: TLS 1.2+ required for all data transmission."
  ],
  soc2: [
    "CONSTRAINT: Implement access logging for all sensitive operations.",
    "CONSTRAINT: Enforce principle of least privilege for all service accounts.",
    "CONSTRAINT: All infrastructure changes must be logged and traceable."
  ],
  gdpr: [
    "CONSTRAINT: Implement data export endpoint for Subject Access Requests.",
    "CONSTRAINT: Implement data deletion endpoint for Right to Erasure.",
    "CONSTRAINT: Log consent timestamps for all data collection."
  ],
  
  // DELIVERY LIFECYCLE
  direct: [
    "INFO: Direct-to-production workflow. Prioritize speed, minimize ceremony."
  ],
  strict: [
    "CONSTRAINT: All new features MUST be wrapped in a feature flag (default: OFF).",
    "CONSTRAINT: Database migrations MUST be backward-compatible and non-destructive.",
    "CONSTRAINT: Support environment-specific configuration (DEV, STAGING, PROD)."
  ],
  
  // SECURITY MODEL
  high_trust: [
    "INFO: Internal tool with trusted users. Standard authentication sufficient."
  ],
  zero_trust: [
    "CRITICAL: Validate JWT on EVERY service call. Do NOT trust API Gateway.",
    "CONSTRAINT: Access tokens expire in 15 minutes max.",
    "CONSTRAINT: Refresh tokens MUST be HttpOnly, Secure, SameSite=Strict.",
    "CONSTRAINT: Implement request signing for service-to-service calls."
  ],
  
  // ARCHITECTURE STYLE
  monolith: [
    "CONSTRAINT: Do NOT create new microservices. Keep business logic in main application.",
    "CONSTRAINT: Prefer in-process function calls over HTTP/queue for internal operations."
  ],
  microservices: [
    "CONSTRAINT: Operations taking >500ms MUST use async queue.",
    "CONSTRAINT: Each bounded context should be a separate deployable service.",
    "CONSTRAINT: Use correlation IDs for distributed tracing across services."
  ],
  
  // QUALITY TIER
  prototype: [
    "INFO: Prototype quality tier. Prioritize speed over test coverage."
  ],
  production: [
    "CONSTRAINT: All new functions MUST have unit tests.",
    "CONSTRAINT: Maintain minimum 70% code coverage.",
    "CONSTRAINT: No console.log or debug statements in production code.",
    "CONSTRAINT: All errors MUST be properly caught and logged."
  ],
  critical: [
    "CRITICAL: Full test pyramid required (unit, integration, E2E).",
    "CONSTRAINT: Mutation testing score >80%.",
    "CONSTRAINT: Security scanning (SAST/DAST) must pass before merge."
  ],
  
  // DATABASE
  postgres: ["ENVIRONMENT: Database is PostgreSQL. Use parameterized queries for all operations."],
  mongodb: ["ENVIRONMENT: Database is MongoDB. Use schema validation and proper indexing."],
  mysql: ["ENVIRONMENT: Database is MySQL. Use parameterized queries, avoid SELECT *."],
  dynamodb: ["ENVIRONMENT: Database is DynamoDB. Design for single-table patterns. Avoid scans."],
  firestore: ["ENVIRONMENT: Database is Firestore. Use proper security rules and batch operations."],
  cosmos_db: ["ENVIRONMENT: Database is Cosmos DB. Design for partition key efficiency."],
  
  // QUEUE
  sqs: ["ENVIRONMENT: Use AWS SQS for async messaging. Implement dead-letter queues."],
  rabbitmq: ["ENVIRONMENT: Use RabbitMQ for messaging. Implement retry with exponential backoff."],
  kafka: ["ENVIRONMENT: Use Kafka for event streaming. Use Avro/Protobuf schemas for messages."],
  pubsub: ["ENVIRONMENT: Use Cloud Pub/Sub for messaging. Implement proper error handling."],
  service_bus: ["ENVIRONMENT: Use Azure Service Bus for messaging. Configure message sessions."],
  
  // WORKFLOW
  trunk_based: ["WORKFLOW: Use trunk-based development. Keep feature branches short-lived (<1 day)."],
  gitflow: ["WORKFLOW: Use GitFlow. Maintain develop, release, and hotfix branches."],
  github_flow: ["WORKFLOW: Use GitHub Flow. Create feature branches, merge via PR."]
};

// Template category labels
const TEMPLATE_LABELS = {
  b2c: 'Consumer App (B2C)',
  b2b_multitenant: 'Enterprise Platform (B2B)',
  hipaa: 'HIPAA Compliance',
  pci: 'PCI-DSS Compliance',
  soc2: 'SOC2 Compliance',
  gdpr: 'GDPR Compliance',
  direct: 'Direct to Production',
  strict: 'Strict Lifecycle',
  high_trust: 'High Trust Security',
  zero_trust: 'Zero Trust Security',
  monolith: 'Monolith Architecture',
  microservices: 'Microservices Architecture',
  prototype: 'Prototype Quality',
  production: 'Production Quality',
  critical: 'Critical System Quality',
  postgres: 'PostgreSQL Database',
  mongodb: 'MongoDB Database',
  mysql: 'MySQL Database',
  dynamodb: 'DynamoDB Database',
  firestore: 'Firestore Database',
  cosmos_db: 'Cosmos DB Database',
  sqs: 'AWS SQS Queue',
  rabbitmq: 'RabbitMQ Queue',
  kafka: 'Kafka Streaming',
  pubsub: 'Cloud Pub/Sub',
  service_bus: 'Azure Service Bus',
  trunk_based: 'Trunk-Based Development',
  gitflow: 'GitFlow Workflow',
  github_flow: 'GitHub Flow',
  _custom: 'Custom Rules'
};

// Group templates by category
const TEMPLATE_GROUPS = [
  { 
    id: 'tenancy', 
    label: 'Tenancy', 
    keys: ['b2c', 'b2b_multitenant'],
    dnaField: 'tenancy_mode'
  },
  { 
    id: 'compliance', 
    label: 'Compliance', 
    keys: ['hipaa', 'pci', 'soc2', 'gdpr'],
    dnaField: 'compliance',
    isArray: true
  },
  { 
    id: 'delivery', 
    label: 'Delivery', 
    keys: ['direct', 'strict'],
    dnaField: 'delivery_lifecycle'
  },
  { 
    id: 'security', 
    label: 'Security', 
    keys: ['high_trust', 'zero_trust'],
    dnaField: 'security_model'
  },
  { 
    id: 'architecture', 
    label: 'Architecture', 
    keys: ['monolith', 'microservices'],
    dnaField: 'architecture_style'
  },
  { 
    id: 'quality', 
    label: 'Quality', 
    keys: ['prototype', 'production', 'critical'],
    dnaField: 'quality_tier'
  },
  { 
    id: 'database', 
    label: 'Database', 
    keys: ['postgres', 'mongodb', 'mysql', 'dynamodb', 'firestore', 'cosmos_db'],
    envField: 'services.database'
  },
  { 
    id: 'messaging', 
    label: 'Messaging', 
    keys: ['sqs', 'rabbitmq', 'kafka', 'pubsub', 'service_bus'],
    envField: 'services.queue'
  },
  { 
    id: 'workflow', 
    label: 'Workflow', 
    keys: ['trunk_based', 'gitflow', 'github_flow'],
    envField: 'workflow.branching'
  }
];

const CurrentArchitectureView = ({ productId, version, infraStatus, isOwner }) => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(['tenancy', 'compliance', 'security']);
  const [publishedVersion, setPublishedVersion] = useState(null);
  
  // Dialog states
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load config for the selected version
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deliveryConfigApi.getConfig(productId);
      const data = response.data?.delivery_config || response.data;
      
      setConfig(data);
      setPublishedVersion(
        data?.constraint_templates?.published_version || 
        data?.infrastructure_status?.published_version ||
        data?.published_version ||
        infraStatus?.published_version
      );
    } catch (error) {
      console.error('Failed to load architecture config:', error);
    } finally {
      setLoading(false);
    }
  }, [productId, infraStatus?.published_version]);

  useEffect(() => {
    if (productId) {
      loadConfig();
    }
  }, [productId, version, loadConfig]);

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(g => g !== groupId)
        : [...prev, groupId]
    );
  };

  // Get nested value from object
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((curr, key) => curr?.[key], obj);
  };

  // Check if a template key is active based on DNA/env config
  const isTemplateActive = (group, key) => {
    if (!config) return false;
    
    const dna = config.project_dna || {};
    const env = config.environment || {};
    
    if (group.dnaField) {
      if (group.isArray) {
        const arr = dna[group.dnaField] || [];
        return arr.includes(key);
      }
      return dna[group.dnaField] === key;
    }
    
    if (group.envField) {
      const value = getNestedValue(env, group.envField);
      if (!value) return false;
      
      // Normalize database/queue values
      const normalizedValue = value.toLowerCase();
      const normalizedKey = key.toLowerCase();
      
      // Check if the value contains or matches the key
      if (normalizedValue.includes(normalizedKey)) return true;
      if (normalizedKey.includes(normalizedValue)) return true;
      
      // Specific mappings
      if (group.envField === 'services.database') {
        if (normalizedValue.includes('postgres') && key === 'postgres') return true;
        if (normalizedValue.includes('mongo') && key === 'mongodb') return true;
        if (normalizedValue.includes('mysql') && key === 'mysql') return true;
        if (normalizedValue.includes('dynamo') && key === 'dynamodb') return true;
        if (normalizedValue.includes('firestore') && key === 'firestore') return true;
        if (normalizedValue.includes('cosmos') && key === 'cosmos_db') return true;
      }
      
      if (group.envField === 'services.queue') {
        if (normalizedValue.includes('sqs') && key === 'sqs') return true;
        if (normalizedValue.includes('rabbit') && key === 'rabbitmq') return true;
        if (normalizedValue.includes('kafka') && key === 'kafka') return true;
        if (normalizedValue.includes('pubsub') && key === 'pubsub') return true;
        if (normalizedValue.includes('service_bus') && key === 'service_bus') return true;
      }
      
      return value === key;
    }
    
    return false;
  };

  // Count active templates in a group
  const countActiveInGroup = (group) => {
    return group.keys.filter(key => isTemplateActive(group, key)).length;
  };

  // Get constraints for a template key
  const getConstraints = (key) => {
    // First check if there are custom published templates
    const customTemplates = config?.constraint_templates?.working || 
                           config?.constraint_templates?.published?.templates || {};
    
    if (customTemplates[key] && customTemplates[key].length > 0) {
      return customTemplates[key];
    }
    
    // Fall back to system defaults
    return SYSTEM_DEFAULT_TEMPLATES[key] || [];
  };

  // Load MCP context preview
  const loadPreview = async () => {
    try {
      setLoadingPreview(true);
      const res = await deliveryConfigApi.getContext(productId);
      setPreview(res.data);
      setShowPreviewDialog(true);
    } catch (err) {
      console.error('Failed to load preview:', err);
      toast.error('Failed to load MCP preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Copy preview to clipboard
  const copyPreview = () => {
    navigator.clipboard.writeText(JSON.stringify(preview, null, 2));
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  // Get constraint style based on prefix
  const getConstraintStyle = (text) => {
    if (text.startsWith('CRITICAL:')) return 'border-red-400 bg-red-50';
    if (text.startsWith('CONSTRAINT:')) return 'border-yellow-400 bg-yellow-50';
    if (text.startsWith('TEST:')) return 'border-purple-400 bg-purple-50';
    if (text.startsWith('ENVIRONMENT:')) return 'border-blue-400 bg-blue-50';
    if (text.startsWith('WORKFLOW:')) return 'border-green-400 bg-green-50';
    return 'border-gray-300 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="current-architecture-view">
      {/* Notice when viewing historical version */}
      {version && publishedVersion && !version.startsWith(String(publishedVersion)) && (
        <Alert className="border-blue-200 bg-blue-50">
          <History className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <span className="font-medium">Viewing historical version</span>
            <p className="text-sm mt-1">
              You&apos;re viewing a snapshot from v{version}. The constraint templates shown below 
              reflect the current configuration. Full version history viewing is coming soon.
            </p>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Header with version info and Preview JSON button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {publishedVersion ? (
            <Badge variant="default" className="bg-green-100 text-green-800">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              v{publishedVersion}
            </Badge>
          ) : (
            <Badge variant="secondary">Draft</Badge>
          )}
        </div>
        
        {/* Preview JSON button */}
        <Button
          variant="outline"
          size="sm"
          onClick={loadPreview}
          disabled={loadingPreview}
          data-testid="preview-json-btn"
        >
          {loadingPreview ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Code className="w-4 h-4 mr-1" />
          )}
          Preview JSON
        </Button>
      </div>

      {/* Configuration Warnings */}
      {config?.warnings && config.warnings.length > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>Configuration Tensions:</strong>
            <ul className="mt-1 list-disc list-inside text-sm">
              {config.warnings.map((w, i) => (
                <li key={i}>{w.replace('WARNING: ', '')}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Provisioned Infrastructure - if complete */}
      {infraStatus?.status === 'complete' && infraStatus?.outputs && Object.keys(infraStatus.outputs).length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-green-800">
              <CheckCircle className="w-4 h-4" />
              Provisioned Infrastructure
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {Object.entries(infraStatus.outputs).map(([key, value]) => (
                <div key={key}>
                  <span className="text-green-700 font-medium capitalize">
                    {key.replace(/_/g, ' ')}:
                  </span>
                  <span className="ml-2 text-green-600 break-all">{value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template groups - Collapsible sections */}
      {TEMPLATE_GROUPS.map((group) => {
        const activeCount = countActiveInGroup(group);
        const hasActive = activeCount > 0;

        return (
          <Card key={group.id}>
            <Collapsible
              open={expandedGroups.includes(group.id)}
              onOpenChange={() => toggleGroup(group.id)}
            >
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">{group.label}</CardTitle>
                    <div className="flex items-center gap-2">
                      {hasActive ? (
                        <Badge variant="default" className="bg-blue-100 text-blue-800 text-xs">
                          {activeCount} active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-500 border-red-300 text-xs">
                          Not configured
                        </Badge>
                      )}
                      {expandedGroups.includes(group.id) ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-3">
                  {group.keys.map((key) => {
                    const constraints = getConstraints(key);
                    const isActive = isTemplateActive(group, key);

                    // Only show active templates
                    if (!isActive) return null;

                    return (
                      <div 
                        key={key} 
                        className="p-3 rounded-lg border bg-blue-50 border-blue-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">
                              {TEMPLATE_LABELS[key] || key}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {constraints.length} constraint{constraints.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Constraint text display */}
                        {constraints.length > 0 ? (
                          <div className="space-y-1">
                            {constraints.map((constraint, idx) => {
                              const text = typeof constraint === 'string' 
                                ? constraint 
                                : constraint.rule || constraint.text || JSON.stringify(constraint);
                              return (
                                <p 
                                  key={idx} 
                                  className={cn(
                                    "text-sm pl-3 py-1 border-l-2 rounded-r",
                                    getConstraintStyle(text)
                                  )}
                                >
                                  {text}
                                </p>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No constraints defined</p>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Show message if nothing active in this group */}
                  {!hasActive && (
                    <p className="text-sm text-gray-500 italic text-center py-2">
                      Configure in &quot;Start a New Version&quot; to activate
                    </p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}

      {/* Custom Rules Section */}
      {config?.constraint_templates?.custom_rules && config.constraint_templates.custom_rules.length > 0 && (
        <Card>
          <Collapsible
            open={expandedGroups.includes('custom')}
            onOpenChange={() => toggleGroup('custom')}
          >
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">Custom Rules</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="bg-orange-100 text-orange-800 text-xs">
                      {config.constraint_templates.custom_rules.length} active
                    </Badge>
                    {expandedGroups.includes('custom') ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-2">
                {config.constraint_templates.custom_rules.map((rule, idx) => (
                  <p 
                    key={idx} 
                    className="text-sm pl-3 py-1 border-l-2 border-orange-400 bg-orange-50 rounded-r"
                  >
                    {typeof rule === 'string' ? rule : rule.rule}
                  </p>
                ))}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      )}

      {/* Preview JSON Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-purple-500" />
              MCP Context Preview
            </DialogTitle>
            <DialogDescription>
              This is the context your AI assistant will receive when connecting via MCP
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              className="absolute top-2 right-2 z-10"
              onClick={copyPreview}
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-auto max-h-96">
              {JSON.stringify(preview, null, 2)}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurrentArchitectureView;
